/**
 * CISA Zero Trust Maturity Model v2.0 (April 2023)
 *
 * This file defines the complete CISA ZTMM v2 structure including:
 * - 5 Pillars: Identity, Devices, Networks, Applications & Workloads, Data
 * - 3 Cross-Cutting Capabilities: Visibility & Analytics, Automation & Orchestration, Governance
 * - Functions per pillar with maturity criteria
 * - Product suggestions for maturity progression
 *
 * Reference: https://www.cisa.gov/zero-trust-maturity-model
 */

// Re-export maturity levels from shared constants for consistency
export { MATURITY_LEVELS, MATURITY_KEYS, MATURITY_COLORS, MATURITY_SCORES } from '../../../config/zeroTrustConstants';

/**
 * Product adoption status options
 */
export const PRODUCT_STATUS = [
  { key: 'IN_USE', label: 'In Use', color: '#10b981', icon: '✓' },
  { key: 'PLANNED', label: 'Planned', color: '#3b82f6', icon: '◐' },
  { key: 'EVALUATING', label: 'Evaluating', color: '#f59e0b', icon: '○' },
  { key: 'NOT_APPLICABLE', label: 'N/A', color: '#6b7280', icon: '—' }
];

/**
 * The 5 CISA ZTMM Pillars
 */
export const CISA_PILLARS = [
  { code: 'IDENTITY', name: 'Identity', color: '#6366f1', icon: 'key', cssVar: 'var(--zt-identity, #6366f1)' },
  { code: 'DEVICES', name: 'Devices', color: '#8b5cf6', icon: 'laptop', cssVar: 'var(--zt-device, #8b5cf6)' },
  { code: 'NETWORKS', name: 'Networks', color: '#06b6d4', icon: 'globe', cssVar: 'var(--zt-network, #06b6d4)' },
  { code: 'APPLICATIONS', name: 'Applications & Workloads', color: '#10b981', icon: 'layers', cssVar: 'var(--zt-application, #10b981)' },
  { code: 'DATA', name: 'Data', color: '#f59e0b', icon: 'database', cssVar: 'var(--zt-data, #f59e0b)' }
];

/**
 * Cross-Cutting Capabilities (apply to all pillars)
 */
export const CROSS_CUTTING = [
  { code: 'VISIBILITY', name: 'Visibility & Analytics', abbrev: 'V&A' },
  { code: 'AUTOMATION', name: 'Automation & Orchestration', abbrev: 'A&O' },
  { code: 'GOVERNANCE', name: 'Governance', abbrev: 'Gov' }
];

/**
 * Functions organized by pillar
 * Each function has a unique code and display name
 *
 * CISA ZTMM v2.0 structure (from Tables 2-6):
 * - Each pillar has 4-5 core functions
 * - Each pillar has 3 cross-cutting capabilities (Visibility, Automation, Governance)
 *   contextualized to that pillar
 *
 * The cross-cutting capabilities are marked with isCrossCutting: true
 */
export const CISA_FUNCTIONS = {
  IDENTITY: [
    // Core Functions (Table 2)
    { code: 'ID_AUTH', name: 'Authentication', order: 1 },
    { code: 'ID_STORES', name: 'Identity Stores', order: 2 },
    { code: 'ID_RISK', name: 'Risk Assessments', order: 3 },
    { code: 'ID_ACCESS', name: 'Access Management', order: 4, isNew: true },
    // Pillar-Specific Cross-Cutting Capabilities
    { code: 'ID_VISIBILITY', name: 'Visibility & Analytics', order: 5, isCrossCutting: true },
    { code: 'ID_AUTOMATION', name: 'Automation & Orchestration', order: 6, isCrossCutting: true },
    { code: 'ID_GOVERNANCE', name: 'Governance', order: 7, isCrossCutting: true }
  ],
  DEVICES: [
    // Core Functions (Table 3)
    { code: 'DEV_POLICY', name: 'Policy Enforcement & Compliance', order: 1, isNew: true },
    { code: 'DEV_ASSET', name: 'Asset & Supply Chain Risk Mgmt', order: 2, isNew: true },
    { code: 'DEV_ACCESS', name: 'Resource Access', order: 3, formerlyNamed: 'Data Access' },
    { code: 'DEV_THREAT', name: 'Device Threat Protection', order: 4, isNew: true },
    // Pillar-Specific Cross-Cutting Capabilities
    { code: 'DEV_VISIBILITY', name: 'Visibility & Analytics', order: 5, isCrossCutting: true },
    { code: 'DEV_AUTOMATION', name: 'Automation & Orchestration', order: 6, isCrossCutting: true },
    { code: 'DEV_GOVERNANCE', name: 'Governance', order: 7, isCrossCutting: true }
  ],
  NETWORKS: [
    // Core Functions (Table 4)
    { code: 'NET_SEG', name: 'Network Segmentation', order: 1 },
    { code: 'NET_TRAFFIC', name: 'Traffic Management', order: 2, isNew: true },
    { code: 'NET_ENCRYPT', name: 'Traffic Encryption', order: 3, formerlyNamed: 'Encryption' },
    { code: 'NET_RESIL', name: 'Network Resilience', order: 4, isNew: true },
    // Pillar-Specific Cross-Cutting Capabilities
    { code: 'NET_VISIBILITY', name: 'Visibility & Analytics', order: 5, isCrossCutting: true },
    { code: 'NET_AUTOMATION', name: 'Automation & Orchestration', order: 6, isCrossCutting: true },
    { code: 'NET_GOVERNANCE', name: 'Governance', order: 7, isCrossCutting: true }
  ],
  APPLICATIONS: [
    // Core Functions (Table 5)
    { code: 'APP_ACCESS', name: 'Application Access', order: 1, formerlyNamed: 'Access Authorization' },
    { code: 'APP_THREAT', name: 'Application Threat Protections', order: 2, formerlyNamed: 'Threat Protections' },
    { code: 'APP_ACCESSIBLE', name: 'Accessible Applications', order: 3, formerlyNamed: 'Accessibility' },
    { code: 'APP_SECDEV', name: 'Secure Development & Deployment', order: 4, isNew: true },
    { code: 'APP_TESTING', name: 'Application Security Testing', order: 5, formerlyNamed: 'Application Security' },
    // Pillar-Specific Cross-Cutting Capabilities
    { code: 'APP_VISIBILITY', name: 'Visibility & Analytics', order: 6, isCrossCutting: true },
    { code: 'APP_AUTOMATION', name: 'Automation & Orchestration', order: 7, isCrossCutting: true },
    { code: 'APP_GOVERNANCE', name: 'Governance', order: 8, isCrossCutting: true }
  ],
  DATA: [
    // Core Functions (Table 6)
    { code: 'DATA_INV', name: 'Data Inventory Management', order: 1 },
    { code: 'DATA_CAT', name: 'Data Categorization', order: 2, isNew: true },
    { code: 'DATA_AVAIL', name: 'Data Availability', order: 3, isNew: true },
    { code: 'DATA_ACCESS', name: 'Data Access', order: 4 },
    { code: 'DATA_ENCRYPT', name: 'Data Encryption', order: 5 },
    // Pillar-Specific Cross-Cutting Capabilities
    { code: 'DATA_VISIBILITY', name: 'Visibility & Analytics', order: 6, isCrossCutting: true },
    { code: 'DATA_AUTOMATION', name: 'Automation & Orchestration', order: 7, isCrossCutting: true },
    { code: 'DATA_GOVERNANCE', name: 'Governance', order: 8, isCrossCutting: true }
  ]
};

/**
 * Get only core functions (excluding pillar-specific cross-cutting capabilities)
 * Use this when you want just the main assessment functions per pillar
 */
export const CISA_CORE_FUNCTIONS = Object.fromEntries(
  Object.entries(CISA_FUNCTIONS).map(([pillar, functions]) => [
    pillar,
    functions.filter(f => !f.isCrossCutting)
  ])
);

/**
 * Get all unique function codes (flattened from all pillars)
 */
export const ALL_FUNCTION_CODES = Object.values(CISA_FUNCTIONS).flat().map(f => f.code);

/**
 * Maturity criteria from CISA ZTMM v2.0 PDF (April 2023)
 * VERBATIM text from Tables 2-7 of the official CISA document.
 * Describes what each maturity level means for each function.
 *
 * Reference: https://www.cisa.gov/zero-trust-maturity-model
 */
export const MATURITY_CRITERIA = {
  // ═══════════════════════════════════════════════════════════════════════════
  // IDENTITY PILLAR (Table 2, Pages 13-15)
  // ═══════════════════════════════════════════════════════════════════════════

  ID_AUTH: {
    TRADITIONAL: 'Agency authenticates identity using either passwords or multi-factor authentication (MFA) with static access for entity identity.',
    INITIAL: 'Agency authenticates identity using MFA, which may include passwords as one factor and requires validation of multiple entity attributes (e.g., locale or activity).',
    ADVANCED: 'Agency begins to authenticate all identity using phishing-resistant MFA and attributes, including initial implementation of passwordless MFA via FIDO2 or PIV.',
    OPTIMAL: 'Agency continuously validates identity with phishing-resistant MFA, not just when access is initially granted.'
  },

  ID_STORES: {
    TRADITIONAL: 'Agency only uses self-managed, on-premises (i.e., planned, deployed, and maintained by agency) identity stores.',
    INITIAL: 'Agency has a combination of self-managed identity stores and hosted identity store(s) (e.g., cloud or other agency) with minimal integration between the store(s) (e.g., Single Sign-on.).',
    ADVANCED: 'Agency begins to securely consolidate and integrate some self-managed and hosted identity stores.',
    OPTIMAL: 'Agency securely integrates their identity stores across all partners and environments as appropriate.'
  },

  ID_RISK: {
    TRADITIONAL: 'Agency makes limited determinations for identity risk (i.e., likelihood that an identity is compromised).',
    INITIAL: 'Agency determines identity risk using manual methods and static rules to support visibility.',
    ADVANCED: 'Agency determines identity risk with some automated analysis and dynamic rules to inform access decisions and response activities.',
    OPTIMAL: 'Agency determines identity risk in real time based on continuous analysis and dynamic rules to deliver ongoing protection.'
  },

  ID_ACCESS: {
    TRADITIONAL: 'Agency authorizes permanent access with periodic review for both privileged and unprivileged accounts.',
    INITIAL: 'Agency authorizes access, including for privileged access requests, that expires with automated review.',
    ADVANCED: 'Agency authorizes need-based and session-based access, including for privileged access request, that is tailored to actions and resources.',
    OPTIMAL: 'Agency uses automation to authorize just-in-time and just-enough access tailored to individual actions and individual resource needs.'
  },

  // Identity Pillar - Cross-Cutting Capabilities
  ID_VISIBILITY: {
    TRADITIONAL: 'Agency collects user and entity activity logs, especially for privileged credentials, and performs some routine manual analysis.',
    INITIAL: 'Agency collects user and entity activity logs and performs routine manual analysis and some automated analysis, with limited correlation between log types.',
    ADVANCED: 'Agency performs automated analysis across some user and entity activity log types and augments collection to address gaps in visibility.',
    OPTIMAL: 'Agency maintains comprehensive visibility and situational awareness across enterprise by performing automated analysis over user activity log types, including behavior-based analytics.'
  },

  ID_AUTOMATION: {
    TRADITIONAL: 'Agency manually orchestrates (onboards, offboards, and disables) self-managed identities (users and entities), with little integration, and performs regular review.',
    INITIAL: 'Agency manually orchestrates privileged and external identities and automates orchestration of non-privileged users and of self-managed entities.',
    ADVANCED: 'Agency manually orchestrates privileged user identities and automates orchestration of all identities with integration across all environments.',
    OPTIMAL: 'Agency automates orchestration of all identities with full integration across all environments based on behaviors, enrollments, and deployment needs.'
  },

  ID_GOVERNANCE: {
    TRADITIONAL: 'Agency implements identity policies (authentication, credentials, access, lifecycle, etc.) with enforcement via static technical mechanisms and manual review.',
    INITIAL: 'Agency defines and begins implementing identity policies for enterprise-wide enforcement with minimal automation and manual updates.',
    ADVANCED: 'Agency implements identity policies for enterprise-wide enforcement with automation and updates policies periodically.',
    OPTIMAL: 'Agency implements and fully automates enterprise-wide identity policies for all users and entities across all systems with continuous enforcement and dynamic updates.'
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // DEVICES PILLAR (Table 3, Pages 17-19)
  // ═══════════════════════════════════════════════════════════════════════════

  DEV_POLICY: {
    TRADITIONAL: 'Agency has limited, if any, visibility (i.e., ability to inspect device behavior) into device compliance with few methods of enforcing policies or managing software, configurations, or vulnerabilities.',
    INITIAL: 'Agency receives self-reported device characteristics (e.g., keys, tokens, users, etc., on the device) but has limited enforcement mechanisms. Agency has a preliminary, basic process in place to approve software use and push updates and configuration changes to devices.',
    ADVANCED: 'Agency has verified insights (i.e., an administrator can inspect and verify the data on device) on initial access to device and enforces compliance for most devices and virtual assets. Agency uses automated methods to manage devices and virtual assets, approve software, and identify vulnerabilities and install patches.',
    OPTIMAL: 'Agency continuously verifies insights and enforces compliance throughout the lifetime of devices and virtual assets. Agency integrates device, software, configuration, and vulnerability management across all agency environments, including for virtual assets.'
  },

  DEV_ASSET: {
    TRADITIONAL: 'Agency does not track physical or virtual assets in an enterprise-wide or cross-vendor manner and manages its own supply chain acquisition of devices and services in ad hoc fashion with a limited view of enterprise risks.',
    INITIAL: 'Agency tracks all physical and some virtual assets and manages supply chain risks by establishing policies and control baselines according to federal recommendations using a robust framework, (e.g., NIST SCRM.)',
    ADVANCED: 'Agency begins to develop a comprehensive enterprise view of physical and virtual assets via automated processes that can function across multiple vendors to verify acquisitions, track development cycles, and provide third-party assessments.',
    OPTIMAL: 'Agency has a comprehensive, at- or near-real-time view of all assets across vendors and service providers, automates its supply chain risk management as applicable, builds operations that tolerate supply chain failures, and incorporates best practices.'
  },

  DEV_ACCESS: {
    TRADITIONAL: 'Agency does not require visibility into devices or virtual assets used to access resources.',
    INITIAL: 'Agency requires some devices or virtual assets to report characteristics then use this information to approve resource access.',
    ADVANCED: 'Agency\'s initial resource access considers verified device or virtual asset insights.',
    OPTIMAL: 'Agency\'s resource access considers real-time risk analytics within devices and virtual assets.'
  },

  DEV_THREAT: {
    TRADITIONAL: 'Agency manually deploys threat protection capabilities to some devices.',
    INITIAL: 'Agency has some automated processes for deploying and updating threat protection capabilities to devices and to virtual assets with limited policy enforcement and compliance monitoring integration.',
    ADVANCED: 'Agency begins to consolidate threat protection capabilities to centralized solutions for devices and virtual assets and integrates most of these capabilities with policy enforcement and compliance monitoring.',
    OPTIMAL: 'Agency has a centralized threat protection security solution(s) deployed with advanced capabilities for all devices and virtual assets and a unified approach for device threat protection, policy enforcement, and compliance monitoring.'
  },

  // Devices Pillar - Cross-Cutting Capabilities
  DEV_VISIBILITY: {
    TRADITIONAL: 'Agency uses a physically labeled inventory and limited software monitoring to review devices on a regular basis with some manual analysis.',
    INITIAL: 'Agency uses digital identifiers (e.g., interface addresses, digital tags) alongside a manual inventory and endpoint monitoring of devices when available. Some agency devices and virtual assets are under automated analysis (e.g., software-based scanning) for anomaly detection based on risk.',
    ADVANCED: 'Agency automates both inventory collection (including endpoint monitoring on all standard user devices, e.g., desktops and laptops, mobile phones, tablets, and their virtual assets) and anomaly detection to detect unauthorized devices.',
    OPTIMAL: 'Agency automates status collection of all network-connected devices and virtual assets while correlating with identities, conducting endpoint monitoring, and performing anomaly detection to inform resource access. Agency tracks patterns of provisioning and/or de-provisioning of virtual assets for anomalies.'
  },

  DEV_AUTOMATION: {
    TRADITIONAL: 'Agency manually provisions, configures, and/or registers devices within the enterprise.',
    INITIAL: 'Agency begins to use tools and scripts to automate the process of provisioning, configuration, registration, and/or deprovisioning for devices and virtual assets.',
    ADVANCED: 'Agency has implemented monitoring and enforcement mechanisms to identify and manually disconnect or isolate non-compliant (vulnerable, unverified certificate; unregistered mac address) devices and virtual assets.',
    OPTIMAL: 'Agency has fully automated processes for provisioning, registering, monitoring, isolating, remediating, and deprovisioning devices and virtual assets.'
  },

  DEV_GOVERNANCE: {
    TRADITIONAL: 'Agency sets some policies for the lifecycle of their traditional and peripheral computing devices and relies on manual processes to maintain (e.g., update, patch, sanitize) these devices.',
    INITIAL: 'Agency sets and enforces policies for the procurement of new devices, the lifecycle of non-traditional computing devices and virtual assets, and for regularly conducting monitoring and scanning of devices.',
    ADVANCED: 'Agency sets enterprise-wide policies for the lifecycle of devices and virtual assets, including their enumeration and accountability, with some automated enforcement mechanisms.',
    OPTIMAL: 'Agency automates policies for the lifecycle of all network-connected devices and virtual assets across the enterprise.'
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // NETWORKS PILLAR (Table 4, Pages 20-22)
  // ═══════════════════════════════════════════════════════════════════════════

  NET_SEG: {
    TRADITIONAL: 'Agency defines their network architecture using large perimeter/macro-segmentation with minimal restrictions on reachability within network segments. Agency may also rely on multi-service interconnections (e.g., bulk traffic VPN tunnels).',
    INITIAL: 'Agency begins to deploy network architecture with the isolation of critical workloads, constraining connectivity to least function principles, and a transition toward service-specific interconnections.',
    ADVANCED: 'Agency expands deployment of endpoint and application profile isolation mechanisms to more of their network architecture with ingress/egress micro-perimeters and service-specific interconnections.',
    OPTIMAL: 'Agency network architecture consists of fully distributed ingress/egress micro-perimeters and extensive micro-segmentation based around application profiles with dynamic just-in-time and just-enough connectivity for service-specific interconnections.'
  },

  NET_TRAFFIC: {
    TRADITIONAL: 'Agency manually implements static network rules and configurations to manage traffic at service provisioning, with limited monitoring capabilities (e.g., application performance monitoring or anomaly detection) and manual audits and reviews of profile changes for mission critical applications.',
    INITIAL: 'Agency establishes application profiles with distinct traffic management features and begins to map all applications to these profiles. Agency expands application of static rules to all applications and performs periodic manual audits of application profile assessments.',
    ADVANCED: 'Agency implements dynamic network rules and configurations for resource optimization that are periodically adapted based upon automated risk-aware and risk-responsive application profile assessments and monitoring.',
    OPTIMAL: 'Agency implements dynamic network rules and configurations that continuously evolve to meet application profile needs and reprioritize applications based on mission criticality, risk, etc.'
  },

  NET_ENCRYPT: {
    TRADITIONAL: 'Agency encrypts minimal traffic and relies on manual or ad hoc processes to manage and secure encryption keys.',
    INITIAL: 'Agency begins to encrypt all traffic to internal applications, to prefer encryption for traffic to external applications, to formalize key management policies, and to secure server/service encryption keys.',
    ADVANCED: 'Agency ensures encryption for all applicable internal and external traffic protocols, manages issuance and rotation of keys and certificates, and begins to incorporate best practices for cryptographic agility.',
    OPTIMAL: 'Agency continues to encrypt traffic as appropriate, enforces least privilege principles for secure key management enterprise-wide, and incorporates best practices for cryptographic agility as widely as possible.'
  },

  NET_RESIL: {
    TRADITIONAL: 'Agency configures network capabilities on a case-by-case basis to only match individual application availability demands with limited resilience mechanisms for workloads not deemed mission critical.',
    INITIAL: 'Agency begins to configure network capabilities to manage availability demands for additional applications and expand resilience mechanisms for workloads not deemed mission critical.',
    ADVANCED: 'Agency has configured network capabilities to dynamically manage the availability demands and resilience mechanisms for the majority of their applications.',
    OPTIMAL: 'Agency integrates holistic delivery and awareness in adapting to changes in availability demands for all workloads and provides proportionate resilience.'
  },

  // Networks Pillar - Cross-Cutting Capabilities
  NET_VISIBILITY: {
    TRADITIONAL: 'Agency incorporates limited boundary-focused network monitoring capabilities with minimal analysis to start developing centralized situational awareness.',
    INITIAL: 'Agency employs network monitoring capabilities based on known indicators of compromise (including network enumeration) to develop situational awareness in each environment and begins to correlate telemetry across traffic types and environments for analysis and threat hunting activities.',
    ADVANCED: 'Agency deploys anomaly-based network detection capabilities to develop situational awareness across all environments, begins to correlate telemetry from multiple sources for analysis, and incorporates automated processes for robust threat hunting activities.',
    OPTIMAL: 'Agency maintains visibility into communication across all agency networks and environments while enabling enterprise-wide situational awareness and advanced monitoring capabilities that automate telemetry correlation across all detection sources.'
  },

  NET_AUTOMATION: {
    TRADITIONAL: 'Agency uses manual processes to manage the configuration and resource lifecycle for agency networks and environments with periodic integration of policy requirements and situational awareness.',
    INITIAL: 'Agency begins using automated methods to manage the configuration and resource lifecycle for some agency networks or environments and ensures that all resources have a defined lifetime based on policies and telemetry.',
    ADVANCED: 'Agency uses automated change management methods (e.g., CI/CD) to manage the configuration and resource lifecycle for all agency networks and environments, responding to and enforcing policies and protections against perceived risks.',
    OPTIMAL: 'Agency networks and environments are defined using infrastructure-as-code managed by automated change management methods, including automated initiation and expiration to align with changing needs.'
  },

  NET_GOVERNANCE: {
    TRADITIONAL: 'Agency implements static network policies (access, protocols, segmentation, alerts, and remediation) with an approach focused on perimeter protections.',
    INITIAL: 'Agency defines and begins to implement policies tailored to individual network segments and resources while also inheriting corporate-wide rules as appropriate.',
    ADVANCED: 'Agency incorporates automation in implementing tailored policies and facilitates the transition from perimeter-focused protections.',
    OPTIMAL: 'Agency implements enterprise-wide network policies that enable tailored, local controls; dynamic updates; and secure external connections based on application and user workflows.'
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // APPLICATIONS AND WORKLOADS PILLAR (Table 5, Pages 23-25)
  // ═══════════════════════════════════════════════════════════════════════════

  APP_ACCESS: {
    TRADITIONAL: 'Agency authorizes access to applications primarily based on local authorization and static attributes.',
    INITIAL: 'Agency begins to implement authorizing access capabilities to applications that incorporate contextual information (e.g., identity, device compliance, and/or other attributes) per request with expiration.',
    ADVANCED: 'Agency automates application access decisions with expanded contextual information and enforced expiration conditions that adhere to least privilege principles.',
    OPTIMAL: 'Agency continuously authorizes application access, incorporating real-time risk analytics and factors such as behavior or usage patterns.'
  },

  APP_THREAT: {
    TRADITIONAL: 'Agency threat protections have minimal integration with application workflows, applying general purpose protections for known threats.',
    INITIAL: 'Agency integrates threat protections into mission critical application workflows, applying protections against known threats and some application-specific threats.',
    ADVANCED: 'Agency integrates threat protections into all application workflows, protecting against some application-specific and targeted threats.',
    OPTIMAL: 'Agency integrates advanced threat protections into all application workflows, offering real-time visibility and content-aware protections against sophisticated attacks tailored to applications.'
  },

  APP_ACCESSIBLE: {
    TRADITIONAL: 'Agency makes some mission critical applications available only over private networks and protected public network connections (e.g., VPN) with monitoring.',
    INITIAL: 'Agency makes some of their applicable mission critical applications available over open public networks to authorized users with need via brokered connections.',
    ADVANCED: 'Agency makes most of their applicable mission critical applications available over open public network connections to authorized users as needed.',
    OPTIMAL: 'Agency makes all applicable applications available over open public networks to authorized users and devices, where appropriate, as needed.'
  },

  APP_SECDEV: {
    TRADITIONAL: 'Agency has ad hoc development, testing, and production environments with non-robust code deployment mechanisms.',
    INITIAL: 'Agency provides infrastructure for development, testing, and production environments (including automation) with formal code deployment mechanisms through CI/CD pipelines and requisite access controls in support of least privilege principles.',
    ADVANCED: 'Agency uses distinct and coordinated teams for development, security, and operations while removing developer access to production environment for code deployment.',
    OPTIMAL: 'Agency leverages immutable workloads where feasible, only allowing changes to take effect through redeployment, and removes administrator access to deployment environments in favor of automated processes for code deployment.'
  },

  APP_TESTING: {
    TRADITIONAL: 'Agency performs application security testing prior to deployment, primarily via manual testing methods.',
    INITIAL: 'Agency begins to use static and dynamic (i.e., application is executing) testing methods to perform security testing, including manual expert analysis, prior to application deployment.',
    ADVANCED: 'Agency integrates application security testing into the application development and deployment process, including the use of periodic dynamic testing methods.',
    OPTIMAL: 'Agency integrates application security testing throughout the software development lifecycle across the enterprise with routine automated testing of deployed applications.'
  },

  // Applications Pillar - Cross-Cutting Capabilities
  APP_VISIBILITY: {
    TRADITIONAL: 'Agency performs some performance and security monitoring of mission critical applications with limited aggregation and analytics.',
    INITIAL: 'Agency begins to automate application profile (e.g., state, health, and performance) and security monitoring for improved log collection, aggregation, and analytics.',
    ADVANCED: 'Agency automates profile and security monitoring for most applications with heuristics to identify application-specific and enterprise-wide trends and refines processes over time to address gaps in visibility.',
    OPTIMAL: 'Agency performs continuous and dynamic monitoring across all applications to maintain enterprise-wide comprehensive visibility.'
  },

  APP_AUTOMATION: {
    TRADITIONAL: 'Agency manually establishes static application hosting location and access at provisioning with limited maintenance and review.',
    INITIAL: 'Agency periodically modifies application configurations (including location and access) to meet relevant security and performance goals.',
    ADVANCED: 'Agency automates application configurations to respond to operational and environmental changes.',
    OPTIMAL: 'Agency automates application configurations to continuously optimize for security and performance.'
  },

  APP_GOVERNANCE: {
    TRADITIONAL: 'Agency relies primarily on manual enforcement policies for application access, development, deployment, software asset management, security testing and evaluation (ST&E) at technology insertion, patching, and tracking software dependencies.',
    INITIAL: 'Agency begins to automate policy enforcement for application development (including access to development infrastructure), deployment, software asset management, ST&E at technology insertion, patching, and tracking software dependencies based upon mission needs (for example, with Software Bill of Materials).',
    ADVANCED: 'Agency implements tiered, tailored policies enterprise-wide for applications and all aspects of the application development and deployment lifecycles and leverages automation, where possible, to support enforcement.',
    OPTIMAL: 'Agency fully automates policies governing applications development and deployment, including incorporating dynamic updates for applications through the CI/CD pipeline.'
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // DATA PILLAR (Table 6, Pages 26-28)
  // ═══════════════════════════════════════════════════════════════════════════

  DATA_INV: {
    TRADITIONAL: 'Agency manually identifies and inventories some agency data (e.g., mission critical data).',
    INITIAL: 'Agency begins to automate data inventory processes for both on-premises and in cloud environments, covering most agency data, and begins to incorporate protections against data loss.',
    ADVANCED: 'Agency automates data inventory and tracking enterprise-wide, covering all applicable agency data, with data loss prevention strategies based upon static attributes and/or labels.',
    OPTIMAL: 'Agency continuously inventories all applicable agency data and employs robust data loss prevention strategies that dynamically block suspected data exfiltration.'
  },

  DATA_CAT: {
    TRADITIONAL: 'Agency employs limited and ad hoc data categorization capabilities.',
    INITIAL: 'Agency begins to implement a data categorization strategy with defined labels and manual enforcement mechanisms.',
    ADVANCED: 'Agency automates some data categorization and labeling processes in a consistent, tiered, targeted manner with simple, structured formats and regular review.',
    OPTIMAL: 'Agency automates data categorization and labeling enterprise-wide with robust techniques; granular, structured formats; and mechanisms to address all data types.'
  },

  DATA_AVAIL: {
    TRADITIONAL: 'Agency primarily makes data available from on-premises data stores with some off-site backups.',
    INITIAL: 'Agency makes some data available from redundant, highly available data stores (e.g., cloud) and maintains off-site backups for on-premises data.',
    ADVANCED: 'Agency primarily makes data available from redundant, highly available data stores and ensures access to historical data.',
    OPTIMAL: 'Agency uses dynamic methods to optimize data availability, including historical data, according to user and entity need.'
  },

  DATA_ACCESS: {
    TRADITIONAL: 'Agency governs user and entity access (e.g., permissions to read, write, copy, grant others access, etc.) to data through static access controls.',
    INITIAL: 'Agency begins to deploy automated data access controls that incorporate elements of least privilege across the enterprise.',
    ADVANCED: 'Agency automates data access controls that consider various attributes such as identity, device risk, application, data category, etc., and are time limited where applicable.',
    OPTIMAL: 'Agency automates dynamic just-in-time and just-enough data access controls enterprise-wide with continuous review of permissions.'
  },

  DATA_ENCRYPT: {
    TRADITIONAL: 'Agency encrypts minimal agency data at rest and in transit and relies on manual or ad hoc processes to manage and secure encryption keys.',
    INITIAL: 'Agency encrypts all data in transit and, where feasible, data at rest (e.g., mission critical data and data stored in external environments) and begins to formalize key management policies and secure encryption keys.',
    ADVANCED: 'Agency encrypts all data at rest and in transit across the enterprise to the maximum extent possible, begins to incorporate cryptographic agility, and protects encryption keys (i.e., secrets are not hard coded and are rotated on a regular basis).',
    OPTIMAL: 'Agency encrypts data in use where appropriate, enforces least privilege principles for secure key management enterprise-wide, and applies encryption using up-to-date standards and cryptographic agility to the extent possible.'
  },

  // Data Pillar - Cross-Cutting Capabilities
  DATA_VISIBILITY: {
    TRADITIONAL: 'Agency has limited visibility into data including location, access, and usage, with analysis consisting primarily of manual processes.',
    INITIAL: 'Agency obtains visibility based on data inventory management, categorization, encryption, and access attempts, with some automated analysis and correlation.',
    ADVANCED: 'Agency maintains data visibility in a more comprehensive, enterprise-wide manner with automated analysis and correlation and begins to employ predictive analytics.',
    OPTIMAL: 'Agency has visibility across the full data lifecycle with robust analytics, including predictive analytics, that support comprehensive views of agency data and continuous security posture assessment.'
  },

  DATA_AUTOMATION: {
    TRADITIONAL: 'Agency implements data lifecycle and security policies (e.g., access, usage, storage, encryption, configurations, protections, backups, categorization, sanitization) through manual, and potentially ad hoc, processes.',
    INITIAL: 'Agency uses some automated processes to implement data lifecycle and security policies.',
    ADVANCED: 'Agency implements data lifecycle and security policies primarily through automated methods for most agency data in a consistent, tiered, targeted manner across the enterprise.',
    OPTIMAL: 'Agency automates, to the maximum extent possible, data lifecycles and security policies for all agency data across the enterprise.'
  },

  DATA_GOVERNANCE: {
    TRADITIONAL: 'Agency relies on ad hoc data governance policies (e.g., for protection, categorization, access, inventorying, storage, recovery, removal, etc.) with manual implementation.',
    INITIAL: 'Agency defines high-level data governance policies and relies primarily on manual, segmented implementation.',
    ADVANCED: 'Agency begins integration of data lifecycle policy enforcement across the enterprise, enabling more unified definitions for data governance policies.',
    OPTIMAL: 'Agency data lifecycle policies are unified to the maximum extent possible and dynamically enforced across the enterprise.'
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // CROSS-CUTTING CAPABILITIES (Table 7, Pages 29-30)
  // Enterprise-level capabilities that span all pillars
  // ═══════════════════════════════════════════════════════════════════════════

  VISIBILITY: {
    TRADITIONAL: 'Agency manually collects limited logs across their enterprise with low fidelity and minimal analysis.',
    INITIAL: 'Agency begins to automate the collection and analysis of logs and events for mission critical functions and regularly assesses processes for gaps in visibility.',
    ADVANCED: 'Agency expands the automated collection of logs and events enterprise-wide (including virtual environments) for centralized analysis that correlates across multiple sources.',
    OPTIMAL: 'Agency maintains comprehensive visibility enterprise-wide via centralized dynamic monitoring and advanced analysis of logs and events.'
  },

  AUTOMATION: {
    TRADITIONAL: 'Agency relies on static and manual processes to orchestrate operations and response activities with limited automation.',
    INITIAL: 'Agency begins automating orchestration and response activities in support of critical mission functions.',
    ADVANCED: 'Agency automates orchestration and response activities enterprise-wide, leveraging contextual information from multiple sources to inform decisions.',
    OPTIMAL: 'Agency orchestration and response activities dynamically respond to enterprise-wide changing requirements and environmental changes.'
  },

  GOVERNANCE: {
    TRADITIONAL: 'Agency implements policies in an ad hoc manner across the enterprise, with policies enforced via manual processes or static technical mechanisms.',
    INITIAL: 'Agency defines and begins implementing policies for enterprise-wide enforcement with minimal automation and manual updates.',
    ADVANCED: 'Agency implements tiered, tailored policies enterprise-wide and leverages automation where possible to support enforcement. Access policy decisions incorporate contextual information from multiple sources.',
    OPTIMAL: 'Agency implements and fully automates enterprise-wide policies that enable tailored local controls with continuous enforcement and dynamic updates.'
  }
};

/**
 * Product suggestions for achieving each maturity level
 * Maps function code → maturity level → array of product suggestions
 */
export const PRODUCT_SUGGESTIONS = {
  // Identity Pillar
  ID_AUTH: {
    INITIAL: ['Okta Verify', 'Microsoft Authenticator', 'Duo Security', 'Google Authenticator'],
    ADVANCED: ['YubiKey (FIDO2)', 'Windows Hello for Business', 'PIV Smartcards', 'Okta FastPass'],
    OPTIMAL: ['Passkeys', 'Beyond Identity', 'BeyondCorp Enterprise', 'Continuous Authentication (UEBA)']
  },
  ID_STORES: {
    INITIAL: ['Microsoft Entra ID', 'Okta Universal Directory', 'Ping Identity'],
    ADVANCED: ['SailPoint IdentityIQ', 'Saviynt', 'One Identity'],
    OPTIMAL: ['Decentralized Identity Solutions', 'Identity Fabric Architecture']
  },
  ID_RISK: {
    INITIAL: ['Microsoft Identity Protection', 'Okta ThreatInsight'],
    ADVANCED: ['Exabeam UEBA', 'Securonix', 'Microsoft Sentinel'],
    OPTIMAL: ['AI-driven Identity Analytics', 'Continuous Risk Scoring Platforms']
  },
  ID_ACCESS: {
    INITIAL: ['Microsoft Entra ID Governance', 'SailPoint', 'Saviynt'],
    ADVANCED: ['CyberArk PAM', 'BeyondTrust', 'HashiCorp Vault'],
    OPTIMAL: ['Policy-Based Access Control', 'Just-In-Time Access Platforms']
  },

  // Devices Pillar
  DEV_POLICY: {
    INITIAL: ['Microsoft Intune', 'Jamf Pro', 'VMware Workspace ONE'],
    ADVANCED: ['Tanium', 'Qualys VMDR', 'Rapid7 InsightVM'],
    OPTIMAL: ['Continuous Compliance Platforms', 'Zero Trust Device Trust']
  },
  DEV_ASSET: {
    INITIAL: ['ServiceNow ITAM', 'Axonius', 'Armis'],
    ADVANCED: ['Tanium Asset', 'Qualys Asset Inventory'],
    OPTIMAL: ['Real-time Asset Intelligence Platforms']
  },
  DEV_ACCESS: {
    INITIAL: ['Zscaler Private Access', 'Palo Alto Prisma Access'],
    ADVANCED: ['Cloudflare Access', 'Cisco Duo Network Gateway'],
    OPTIMAL: ['Continuous Device Trust Verification']
  },
  DEV_THREAT: {
    INITIAL: ['CrowdStrike Falcon', 'Microsoft Defender for Endpoint', 'SentinelOne'],
    ADVANCED: ['Palo Alto Cortex XDR', 'Carbon Black'],
    OPTIMAL: ['AI-Driven XDR', 'Autonomous Endpoint Protection']
  },

  // Networks Pillar
  NET_SEG: {
    INITIAL: ['Cisco ISE', 'Aruba ClearPass', 'Fortinet FortiGate'],
    ADVANCED: ['Illumio Core', 'Guardicore (Akamai)', 'VMware NSX'],
    OPTIMAL: ['Zero Trust Network Fabric', 'Intent-Based Networking']
  },
  NET_TRAFFIC: {
    INITIAL: ['Cisco Stealthwatch', 'ExtraHop Reveal(x)', 'Darktrace'],
    ADVANCED: ['Vectra AI', 'Corelight'],
    OPTIMAL: ['AI-Native NDR', 'Encrypted Traffic Analytics']
  },
  NET_ENCRYPT: {
    INITIAL: ['HashiCorp Vault (PKI)', 'Venafi', 'DigiCert'],
    ADVANCED: ['Istio Service Mesh', 'Linkerd', 'Consul Connect'],
    OPTIMAL: ['Post-Quantum Cryptography Solutions', 'Universal mTLS']
  },
  NET_RESIL: {
    INITIAL: ['Cloudflare DDoS Protection', 'AWS Shield', 'Akamai'],
    ADVANCED: ['Multi-Cloud Load Balancing', 'Global Traffic Management'],
    OPTIMAL: ['Self-Healing Network Architecture', 'Chaos Engineering Tools']
  },

  // Applications Pillar
  APP_ACCESS: {
    INITIAL: ['Okta', 'Ping Identity', 'Microsoft Entra ID'],
    ADVANCED: ['Zscaler ZPA', 'Cloudflare Access', 'Palo Alto Prisma Access'],
    OPTIMAL: ['Continuous Authorization Platforms', 'Transaction-Level Access Control']
  },
  APP_THREAT: {
    INITIAL: ['Cloudflare WAF', 'AWS WAF', 'Imperva'],
    ADVANCED: ['Contrast Security RASP', 'Signal Sciences (Fastly)'],
    OPTIMAL: ['AI-Native App Protection', 'Autonomous WAF']
  },
  APP_ACCESSIBLE: {
    INITIAL: ['Zscaler Private Access', 'Cloudflare Access'],
    ADVANCED: ['Palo Alto Prisma Access', 'Netskope Private Access'],
    OPTIMAL: ['Universal ZTNA', 'Seamless Secure Access']
  },
  APP_SECDEV: {
    INITIAL: ['Snyk', 'Checkmarx', 'Veracode'],
    ADVANCED: ['GitGuardian', 'Semgrep', 'Bridgecrew'],
    OPTIMAL: ['Security Mesh Architecture', 'DevSecOps Automation']
  },
  APP_TESTING: {
    INITIAL: ['Qualys WAS', 'OWASP ZAP', 'Burp Suite'],
    ADVANCED: ['HackerOne', 'Bugcrowd', 'Synack'],
    OPTIMAL: ['Continuous Security Validation', 'Automated Red Team Platforms']
  },

  // Data Pillar
  DATA_INV: {
    INITIAL: ['BigID', 'OneTrust', 'Varonis'],
    ADVANCED: ['Alation', 'Collibra', 'Informatica'],
    OPTIMAL: ['Real-Time Data Intelligence', 'Automated Data Lineage']
  },
  DATA_CAT: {
    INITIAL: ['Microsoft Purview', 'Google Cloud DLP', 'AWS Macie'],
    ADVANCED: ['BigID Classification', 'Varonis Data Classification'],
    OPTIMAL: ['AI-Driven Classification', 'Context-Aware Labeling']
  },
  DATA_AVAIL: {
    INITIAL: ['Veeam', 'Cohesity', 'Rubrik'],
    ADVANCED: ['Multi-Region Replication', 'Active-Active DR'],
    OPTIMAL: ['Zero-Downtime Architecture', 'Continuous DR Validation']
  },
  DATA_ACCESS: {
    INITIAL: ['Varonis DatAdvantage', 'Netwrix', 'StealthBits'],
    ADVANCED: ['Immuta', 'Privacera', 'Okera'],
    OPTIMAL: ['Dynamic Data Masking', 'Real-Time Data Access Control']
  },
  DATA_ENCRYPT: {
    INITIAL: ['HashiCorp Vault', 'AWS KMS', 'Azure Key Vault'],
    ADVANCED: ['Thales CipherTrust', 'Fortanix'],
    OPTIMAL: ['Post-Quantum Key Management', 'Homomorphic Encryption']
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // PILLAR-SPECIFIC CROSS-CUTTING CAPABILITIES
  // ═══════════════════════════════════════════════════════════════════════════

  // Identity Pillar Cross-Cutting
  ID_VISIBILITY: {
    INITIAL: ['Splunk', 'Microsoft Sentinel', 'Microsoft Entra ID Audit Logs'],
    ADVANCED: ['Exabeam UEBA', 'Securonix', 'Microsoft Defender for Identity'],
    OPTIMAL: ['Identity Analytics Platform', 'Behavior-Based Identity Analytics']
  },
  ID_AUTOMATION: {
    INITIAL: ['Microsoft Entra ID Lifecycle', 'SailPoint IdentityNow', 'Okta Workflows'],
    ADVANCED: ['Saviynt', 'One Identity', 'CyberArk Identity'],
    OPTIMAL: ['Full Identity Automation Platform', 'AI-Driven Identity Orchestration']
  },
  ID_GOVERNANCE: {
    INITIAL: ['Microsoft Entra ID Governance', 'SailPoint', 'Saviynt'],
    ADVANCED: ['CyberArk Identity Security', 'Omada Identity'],
    OPTIMAL: ['Identity Policy as Code', 'Continuous Identity Governance']
  },

  // Devices Pillar Cross-Cutting
  DEV_VISIBILITY: {
    INITIAL: ['Microsoft Intune', 'Jamf Pro', 'Tanium Asset'],
    ADVANCED: ['Axonius', 'Armis', 'Qualys VMDR'],
    OPTIMAL: ['Real-time Asset Intelligence', 'AI-Driven Device Analytics']
  },
  DEV_AUTOMATION: {
    INITIAL: ['Microsoft Intune', 'VMware Workspace ONE', 'Jamf Pro'],
    ADVANCED: ['Tanium', 'BigFix', 'Puppet'],
    OPTIMAL: ['Full Device Lifecycle Automation', 'Self-Healing Endpoints']
  },
  DEV_GOVERNANCE: {
    INITIAL: ['Microsoft Intune Compliance', 'VMware Workspace ONE'],
    ADVANCED: ['Tanium Comply', 'Qualys Policy Compliance'],
    OPTIMAL: ['Device Policy as Code', 'Continuous Device Compliance']
  },

  // Networks Pillar Cross-Cutting
  NET_VISIBILITY: {
    INITIAL: ['Cisco Secure Network Analytics', 'ExtraHop Reveal(x)', 'Darktrace'],
    ADVANCED: ['Vectra AI', 'Corelight', 'Gigamon'],
    OPTIMAL: ['AI-Native NDR', 'Enterprise-Wide Network Visibility Platform']
  },
  NET_AUTOMATION: {
    INITIAL: ['Cisco DNA Center', 'Ansible', 'Terraform'],
    ADVANCED: ['HashiCorp Terraform Enterprise', 'Cisco NSO'],
    OPTIMAL: ['Infrastructure as Code Platform', 'Self-Configuring Network']
  },
  NET_GOVERNANCE: {
    INITIAL: ['Cisco ISE Policy', 'Palo Alto Panorama'],
    ADVANCED: ['AlgoSec', 'Tufin'],
    OPTIMAL: ['Network Policy as Code', 'Continuous Network Compliance']
  },

  // Applications Pillar Cross-Cutting
  APP_VISIBILITY: {
    INITIAL: ['Datadog APM', 'New Relic', 'Dynatrace'],
    ADVANCED: ['Splunk APM', 'AppDynamics'],
    OPTIMAL: ['Unified Application Observability', 'AI-Driven App Analytics']
  },
  APP_AUTOMATION: {
    INITIAL: ['GitHub Actions', 'GitLab CI/CD', 'Jenkins'],
    ADVANCED: ['ArgoCD', 'Flux', 'Harness'],
    OPTIMAL: ['GitOps Platform', 'Full Application Lifecycle Automation']
  },
  APP_GOVERNANCE: {
    INITIAL: ['SonarQube', 'Snyk', 'Checkmarx'],
    ADVANCED: ['GitHub Advanced Security', 'Veracode'],
    OPTIMAL: ['Application Policy as Code', 'Continuous App Compliance']
  },

  // Data Pillar Cross-Cutting
  DATA_VISIBILITY: {
    INITIAL: ['BigID', 'Varonis', 'Microsoft Purview'],
    ADVANCED: ['Alation', 'Collibra', 'Informatica'],
    OPTIMAL: ['Data Intelligence Platform', 'Predictive Data Analytics']
  },
  DATA_AUTOMATION: {
    INITIAL: ['Microsoft Purview', 'AWS Macie', 'Google Cloud DLP'],
    ADVANCED: ['BigID Automation', 'Varonis Automation'],
    OPTIMAL: ['Full Data Lifecycle Automation', 'Self-Managing Data Platform']
  },
  DATA_GOVERNANCE: {
    INITIAL: ['Collibra', 'Alation', 'Microsoft Purview Governance'],
    ADVANCED: ['Informatica MDM', 'Talend'],
    OPTIMAL: ['Data Policy as Code', 'Continuous Data Governance']
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // ENTERPRISE-LEVEL CROSS-CUTTING (Table 7)
  // ═══════════════════════════════════════════════════════════════════════════

  VISIBILITY: {
    INITIAL: ['Splunk', 'Microsoft Sentinel', 'Elastic SIEM'],
    ADVANCED: ['Chronicle (Google)', 'Sumo Logic', 'LogRhythm'],
    OPTIMAL: ['AI-Native Security Analytics', 'Unified Security Platform']
  },
  AUTOMATION: {
    INITIAL: ['Palo Alto XSOAR', 'Splunk SOAR', 'Swimlane'],
    ADVANCED: ['Tines', 'Torq', 'D3 Security'],
    OPTIMAL: ['Autonomous Security Operations', 'AI-Driven Orchestration']
  },
  GOVERNANCE: {
    INITIAL: ['ServiceNow GRC', 'RSA Archer', 'OneTrust'],
    ADVANCED: ['Drata', 'Vanta', 'Anecdotes'],
    OPTIMAL: ['Policy as Code', 'Continuous Compliance Automation']
  }
};

/**
 * Configuration steps that move an organization from one maturity level to the next.
 *
 * Authored fully for the Networks pillar (workshop reference). Other pillars
 * fall back to an empty array; the UI surfaces this as "Configurations not yet
 * authored" so the workshop facilitator knows to fill them in.
 *
 * Each entry: { vendor, product, steps } — vendor-agnostic where possible.
 */
export const MATURITY_CONFIGURATIONS = {
  // Networks Pillar — fully authored as the workshop reference template
  NET_SEG: {
    TRADITIONAL: [
      { vendor: 'Cisco', product: 'Catalyst access switches', steps: 'Default VLAN1 across all ports. No inter-VLAN ACLs. East-west traffic implicitly trusted inside the campus.' },
      { vendor: 'Multi-vendor', product: 'Perimeter firewall (ASA / FTD / NGFW)', steps: 'Stateful inspection at the internet edge only. Internal-to-internal traffic permitted by default.' }
    ],
    INITIAL: [
      { vendor: 'Cisco', product: 'Catalyst + ASA/FTD', steps: 'Define VLANs per business function (HR, Finance, IT, Guest). Apply inter-VLAN ACLs at the L3 gateway. Enforce stateful inspection between zones.' },
      { vendor: 'Palo Alto / Fortinet', product: 'NGFW with security zones', steps: 'Map VLANs to security zones (Trust / Untrust / DMZ). Build App-ID / application-control rules between zones. Log all denies.' },
      { vendor: 'Cisco', product: 'ISE (basic 802.1X)', steps: 'RADIUS-authenticate switch ports. Static VLAN assignment per AAA group. Quarantine VLAN for unknown devices.' }
    ],
    ADVANCED: [
      { vendor: 'Cisco', product: 'ISE + TrustSec (SGT/SGACL)', steps: 'Tag endpoints with Security Group Tags at the access layer. Author SGACLs for east-west microsegmentation policy. Propagate SGTs via SXP across campus, WAN, and DC.' },
      { vendor: 'Cisco', product: 'Catalyst 9300/9400 + DNA Center (SD-Access)', steps: 'Enable fabric mode. Group endpoints into Virtual Networks and Scalable Groups. Push fabric-wide microseg policy from DNA Center.' },
      { vendor: 'Illumio / VMware NSX / Akamai Guardicore', product: 'Host-based microsegmentation', steps: 'Deploy agents on every workload. Build allow-list policies per workload role. Block all unspecified east-west flows.' }
    ],
    OPTIMAL: [
      { vendor: 'Cisco', product: 'SD-Access + ISE + Secure Network Analytics', steps: 'Identity-derived SGTs assigned in real time from posture and risk signals. Policy auto-adjusts on user/device risk changes. Quarantine via Adaptive Network Control on posture failure.' },
      { vendor: 'Cisco', product: 'Hypershield (AI-native segmentation)', steps: 'Workload-attached enforcement points. Auto-generated policy from observed flows. Segmentation follows workloads across hybrid cloud.' },
      { vendor: 'Illumio + IdP integration', product: 'Continuous policy generation', steps: 'Policies regenerated continuously from observed traffic and identity context. Risk-based deny on posture drift, no human in the loop.' }
    ]
  },
  NET_TRAFFIC: {
    TRADITIONAL: [
      { vendor: 'Multi-vendor', product: 'Firewall syslog + SPAN', steps: 'Forward perimeter firewall logs to a syslog collector. Mirror critical egress to an analyst workstation. Manual review for alerts.' }
    ],
    INITIAL: [
      { vendor: 'Cisco', product: 'Stealthwatch / Secure Network Analytics', steps: 'Enable NetFlow / IPFIX export on all routers and L3 switches. Ingest into Stealthwatch. Turn on baseline anomaly detection.' },
      { vendor: 'Splunk / Microsoft Sentinel', product: 'SIEM correlation', steps: 'Ship network traffic logs into the SIEM. Build correlation rules for known TTPs (lateral movement, data exfil patterns).' }
    ],
    ADVANCED: [
      { vendor: 'Cisco', product: 'Secure Network Analytics + Encrypted Traffic Analytics', steps: 'Enable ETA on Catalyst 9000-series switches. Detect malware-in-TLS via flow-feature analysis without decryption.' },
      { vendor: 'Vectra AI / Darktrace', product: 'NDR with behavioral baselines', steps: 'Deploy sensors at core / aggregation tiers. Build per-host behavioral baselines. Alert on deviations.' }
    ],
    OPTIMAL: [
      { vendor: 'Cisco', product: 'XDR + Secure Network Analytics + ISE', steps: 'Correlate network, endpoint, and identity telemetry in XDR. Automated host quarantine via ISE Adaptive Network Control on high-confidence detections.' },
      { vendor: 'Vectra / Darktrace + SOAR', product: 'Auto-response playbook', steps: 'SOAR playbook auto-isolates a host above a confidence threshold. Analyst reviews post-action, not pre-action.' }
    ]
  },
  NET_ENCRYPT: {
    TRADITIONAL: [
      { vendor: 'Multi-vendor', product: 'External-only TLS', steps: 'HTTPS enforced for internet-facing services. Internal HTTP, SMB, and FTP allowed unencrypted.' }
    ],
    INITIAL: [
      { vendor: 'Cisco / Multi-vendor', product: 'IPsec WAN + TLS 1.2 enforcement', steps: 'Mandate TLS 1.2+ for external services. IPsec site-to-site tunnels for WAN links. Deprecate cleartext protocols on a published timeline.' },
      { vendor: 'HashiCorp Vault', product: 'Internal PKI', steps: 'Stand up an internal CA. Issue short-lived certs for any service capable of mTLS.' }
    ],
    ADVANCED: [
      { vendor: 'Istio / Linkerd', product: 'Service mesh mTLS', steps: 'Enable STRICT mTLS mode across all namespaces. Rotate workload certs every 24h.' },
      { vendor: 'Cisco', product: 'Catalyst MACsec', steps: 'Turn on MACsec (AES-256-GCM) on switch trunks and uplinks. Line-rate encryption with no application impact.' },
      { vendor: 'Venafi / DigiCert', product: 'Certificate lifecycle automation', steps: 'Centralize cert lifecycle. Auto-rotate certs at < 30 days remaining. Block manual cert issuance.' }
    ],
    OPTIMAL: [
      { vendor: 'SPIFFE / SPIRE + service mesh', product: 'Workload identity', steps: 'Workload identity (SPIFFE IDs) drives cert issuance. Zero standing trust between services. Identity, not network position, gates access.' },
      { vendor: 'HashiCorp Vault + PQ-ready PKI', product: 'Post-quantum migration', steps: 'Issue dual-stack (classical + ML-DSA) certificates. Roll all internal CAs to post-quantum-ready algorithms by published deadline.' }
    ]
  },
  NET_RESIL: {
    TRADITIONAL: [
      { vendor: 'Cisco', product: 'HSRP / VRRP', steps: 'First-hop gateway redundancy on critical VLANs. Single ISP. Failover steps live in a runbook, not in code.' }
    ],
    INITIAL: [
      { vendor: 'Cisco', product: 'BGP / OSPF dual-ISP', steps: 'Active-active dual-ISP termination via BGP. Equal-cost multipath. Tested annual failover.' },
      { vendor: 'Cloudflare / AWS Shield Standard', product: 'L3/L4 DDoS protection', steps: 'Deploy in front of all public services. Standard always-on L3/L4 DDoS mitigation.' }
    ],
    ADVANCED: [
      { vendor: 'Cisco', product: 'SD-WAN / vManage', steps: 'Define application-aware path SLAs. Dynamic steering across MPLS / broadband / 5G based on real-time path quality.' },
      { vendor: 'Cloudflare Magic Transit / Akamai Prolexic', product: 'L7 DDoS scrubbing', steps: 'Always-on L7 scrubbing with anycast. Tested quarterly with synthetic attack drills.' }
    ],
    OPTIMAL: [
      { vendor: 'Cisco', product: 'SD-WAN + ThousandEyes', steps: 'Continuous synthetic path probing across the WAN. Auto-remediation triggered on path quality degradation, before users notice.' },
      { vendor: 'Gremlin / Chaos Mesh', product: 'Chaos engineering', steps: 'Quarterly chaos drills in production. Failures injected during business hours by design. Post-drill action items tracked to closure.' }
    ]
  }

  // Other pillars: not yet authored — UI will show "Configuration steps not yet authored" placeholder.
  // Author per workshop. Pattern: { FUNCTION_CODE: { LEVEL: [{ vendor, product, steps }] } }
};

/**
 * Helper: Get configurations for a function/level
 */
export function getConfigurations(functionCode, level) {
  return MATURITY_CONFIGURATIONS[functionCode]?.[level] || [];
}

/**
 * Helper: Get the full ladder of details for a function — criteria + technologies +
 * configurations across all 4 maturity levels. Used by the inline cell-detail panel.
 */
export function getCellLadder(functionCode) {
  const levels = ['TRADITIONAL', 'INITIAL', 'ADVANCED', 'OPTIMAL'];
  return levels.map(level => ({
    level,
    criteria: MATURITY_CRITERIA[functionCode]?.[level] || '',
    technologies: PRODUCT_SUGGESTIONS[functionCode]?.[level] || [],
    configurations: MATURITY_CONFIGURATIONS[functionCode]?.[level] || []
  }));
}

/**
 * Helper: Get all functions as a flat array with pillar info
 */
export function getAllFunctions() {
  const functions = [];
  for (const [pillarCode, pillarFunctions] of Object.entries(CISA_FUNCTIONS)) {
    const pillar = CISA_PILLARS.find(p => p.code === pillarCode);
    for (const fn of pillarFunctions) {
      functions.push({
        ...fn,
        pillarCode,
        pillarName: pillar?.name,
        pillarColor: pillar?.color
      });
    }
  }
  // Add cross-cutting as functions that span all pillars
  for (const cc of CROSS_CUTTING) {
    functions.push({
      code: cc.code,
      name: cc.name,
      isCrossCutting: true,
      order: 100 + CROSS_CUTTING.indexOf(cc)
    });
  }
  return functions;
}

/**
 * Helper: Get maturity criteria for a function/level
 */
export function getMaturityCriteria(functionCode, maturityLevel) {
  return MATURITY_CRITERIA[functionCode]?.[maturityLevel] || 'No criteria defined';
}

/**
 * Helper: Get the next maturity level
 */
export function getNextLevel(currentLevel) {
  const levels = ['TRADITIONAL', 'INITIAL', 'ADVANCED', 'OPTIMAL'];
  const idx = levels.indexOf(currentLevel);
  return idx < levels.length - 1 ? levels[idx + 1] : null;
}

/**
 * Helper: Get product suggestions for the next level
 */
export function getNextLevelSuggestions(functionCode, currentLevel) {
  const nextLevel = getNextLevel(currentLevel);
  if (!nextLevel) return [];
  return PRODUCT_SUGGESTIONS[functionCode]?.[nextLevel] || [];
}

/**
 * Helper: Calculate pillar score (average of function scores within a pillar)
 */
export function calculatePillarScore(pillarCode, assessments) {
  const functions = CISA_FUNCTIONS[pillarCode] || [];
  if (functions.length === 0) return 0;

  const scores = functions.map(fn => {
    const level = assessments[fn.code] || 'TRADITIONAL';
    const scoreMap = { TRADITIONAL: 1, INITIAL: 2, ADVANCED: 3, OPTIMAL: 4 };
    return scoreMap[level] || 1;
  });

  return scores.reduce((sum, s) => sum + s, 0) / scores.length;
}

/**
 * Helper: Get display color for a maturity level
 */
export function getMaturityColor(level) {
  const colors = {
    TRADITIONAL: '#6b7280',
    INITIAL: '#eab308',
    ADVANCED: '#3b82f6',
    OPTIMAL: '#10b981'
  };
  return colors[level] || colors.TRADITIONAL;
}

/**
 * Simple configuration steps for pillars not yet authored with vendor-specific configs.
 * Used as a fallback when MATURITY_CONFIGURATIONS doesn't have vendor details.
 */
const SIMPLE_CONFIGURATIONS = {
  // Identity Pillar
  ID_AUTH: {
    INITIAL: ['Deploy MFA for all user accounts', 'Implement password complexity requirements', 'Enable account lockout policies'],
    ADVANCED: ['Deploy phishing-resistant MFA (FIDO2, PIV)', 'Enforce hardware security keys for privileged accounts', 'Integrate with SSO for unified authentication'],
    OPTIMAL: ['Enable continuous authentication throughout sessions', 'Implement real-time risk signal integration', 'Deploy passwordless authentication universally']
  },
  ID_STORES: {
    INITIAL: ['Consolidate identity stores where possible', 'Implement automated provisioning for new users', 'Establish identity lifecycle policies'],
    ADVANCED: ['Establish authoritative identity source', 'Automate complete identity lifecycle management', 'Implement identity federation across systems'],
    OPTIMAL: ['Deploy unified identity fabric with real-time sync', 'Enable self-service with appropriate controls', 'Implement decentralized identity support']
  },
  ID_RISK: {
    INITIAL: ['Implement basic risk scoring for high-privilege accounts', 'Conduct periodic manual risk assessments', 'Document risk assessment procedures'],
    ADVANCED: ['Deploy continuous identity risk scoring', 'Integrate behavioral analytics with access decisions', 'Automate risk-based access adjustments'],
    OPTIMAL: ['Implement ML/AI-driven identity risk assessment', 'Enable dynamic permission adjustments across all resources', 'Deploy predictive identity threat detection']
  },
  ID_ACCESS: {
    INITIAL: ['Implement RBAC across applications', 'Establish periodic access certification process', 'Deploy JIT access for privileged accounts'],
    ADVANCED: ['Implement ABAC policies with multiple attributes', 'Extend JIT access to most privileged operations', 'Automate regular access reviews'],
    OPTIMAL: ['Deploy dynamic access policies with continuous evaluation', 'Eliminate standing privileges', 'Implement real-time context-based access decisions']
  },
  // Devices Pillar
  DEV_POLICY: {
    INITIAL: ['Define device compliance policies', 'Implement regular compliance checking', 'Establish remediation procedures for non-compliance'],
    ADVANCED: ['Deploy real-time compliance monitoring', 'Automate remediation or access restriction', 'Integrate compliance with access decisions'],
    OPTIMAL: ['Enable continuous device posture assessment', 'Implement dynamic policy enforcement', 'Deploy predictive compliance management']
  },
  DEV_ASSET: {
    INITIAL: ['Implement centralized asset inventory', 'Establish supply chain awareness program', 'Schedule regular inventory updates'],
    ADVANCED: ['Deploy automated asset discovery', 'Implement supply chain risk tracking', 'Enable near real-time visibility'],
    OPTIMAL: ['Achieve complete real-time asset visibility', 'Integrate full supply chain risk management', 'Automate asset lifecycle management']
  },
  DEV_ACCESS: {
    INITIAL: ['Verify device identity before granting access', 'Deploy device certificates or tokens', 'Implement basic device trust policies'],
    ADVANCED: ['Integrate device health into access decisions', 'Implement granular resource access based on trust', 'Deploy device trust scoring'],
    OPTIMAL: ['Enable continuous device trust evaluation', 'Implement dynamic access adjustments in real-time', 'Deploy zero trust network access for all devices']
  },
  DEV_THREAT: {
    INITIAL: ['Deploy EDR on all managed devices', 'Enable automated threat detection', 'Implement basic automated response'],
    ADVANCED: ['Deploy XDR integration across endpoints', 'Enable behavioral threat detection', 'Implement automated containment'],
    OPTIMAL: ['Deploy AI-driven threat protection', 'Enable predictive threat capabilities', 'Integrate with ZT access decisions']
  },
  // Applications Pillar
  APP_ACCESS: {
    INITIAL: ['Implement application-level authentication', 'Deploy basic authorization controls', 'Begin SSO implementation'],
    ADVANCED: ['Enable context-aware application access', 'Implement fine-grained authorization', 'Complete SSO with MFA deployment'],
    OPTIMAL: ['Deploy continuous authorization per transaction', 'Implement real-time risk-based access', 'Optimize user experience with strong security']
  },
  APP_THREAT: {
    INITIAL: ['Deploy WAF with custom rules', 'Implement basic runtime protection', 'Enable automated blocking'],
    ADVANCED: ['Deploy RASP integration', 'Implement API security gateways', 'Enable behavioral threat detection'],
    OPTIMAL: ['Deploy AI-driven app threat protection', 'Enable predictive threat prevention', 'Integrate with security operations']
  },
  APP_ACCESSIBLE: {
    INITIAL: ['Make select applications accessible without VPN', 'Begin ZTNA deployment for key applications', 'Document application access requirements'],
    ADVANCED: ['Extend ZTNA to most applications', 'Implement consistent access model', 'Enable location-independent access'],
    OPTIMAL: ['Achieve universal secure access from anywhere', 'Complete ZTNA deployment', 'Make network location irrelevant']
  },
  APP_SECDEV: {
    INITIAL: ['Integrate security into SDLC', 'Add automated security testing in CI/CD', 'Provide security training for developers'],
    ADVANCED: ['Implement shift-left security fully', 'Add automated security gates in pipeline', 'Secure infrastructure as code'],
    OPTIMAL: ['Achieve security by design', 'Deploy fully automated secure pipeline', 'Enable continuous compliance verification']
  },
  APP_TESTING: {
    INITIAL: ['Deploy regular SAST/DAST scanning', 'Establish vulnerability management program', 'Prioritize remediation workflows'],
    ADVANCED: ['Enable continuous automated testing', 'Deploy SCA for dependencies', 'Consider bug bounty program'],
    OPTIMAL: ['Deploy real-time security testing in production', 'Enable automated exploitation testing', 'Achieve full coverage with intelligent prioritization']
  },
  // Data Pillar
  DATA_INV: {
    INITIAL: ['Initiate basic data inventory', 'Catalog known repositories', 'Begin automated discovery'],
    ADVANCED: ['Achieve comprehensive data inventory', 'Automate data discovery fully', 'Implement data lineage tracking'],
    OPTIMAL: ['Deploy real-time inventory with full lineage', 'Automate updates as data moves', 'Achieve complete visibility across environments']
  },
  DATA_CAT: {
    INITIAL: ['Define classification policy', 'Begin automated classification', 'Apply labels to new data'],
    ADVANCED: ['Automate classification for most data', 'Ensure consistent labeling across repos', 'Drive controls from classification'],
    OPTIMAL: ['Deploy AI-driven continuous classification', 'Enable real-time reclassification', 'Integrate fully with access controls']
  },
  DATA_AVAIL: {
    INITIAL: ['Define RPO/RTO targets', 'Implement regular backups with testing', 'Begin automation of recovery processes'],
    ADVANCED: ['Automate backup and recovery', 'Deploy multi-region data availability', 'Conduct regular DR testing'],
    OPTIMAL: ['Achieve zero-downtime data availability', 'Automate failover and recovery completely', 'Enable continuous DR validation']
  },
  DATA_ACCESS: {
    INITIAL: ['Implement data-level access controls', 'Deploy basic DLP policies', 'Schedule regular access reviews'],
    ADVANCED: ['Deploy fine-grained data access policies', 'Enable context-aware access decisions', 'Automate access governance'],
    OPTIMAL: ['Implement dynamic data access based on context', 'Achieve zero trust data access', 'Deploy full audit trail with analytics']
  },
  DATA_ENCRYPT: {
    INITIAL: ['Define encryption policy', 'Establish key management', 'Encrypt sensitive data at rest'],
    ADVANCED: ['Achieve full encryption at rest', 'Automate key rotation', 'Encrypt all data in transit'],
    OPTIMAL: ['Deploy universal encryption with central key mgmt', 'Prepare for post-quantum cryptography', 'Integrate encryption with access controls']
  },
  // ═══════════════════════════════════════════════════════════════════════════
  // PILLAR-SPECIFIC CROSS-CUTTING CAPABILITIES
  // ═══════════════════════════════════════════════════════════════════════════

  // Identity Pillar Cross-Cutting
  ID_VISIBILITY: {
    INITIAL: ['Collect user and entity activity logs', 'Perform routine manual and some automated analysis', 'Establish correlation between log types'],
    ADVANCED: ['Perform automated analysis across user activity log types', 'Augment collection to address visibility gaps', 'Enable cross-pillar identity visibility'],
    OPTIMAL: ['Maintain comprehensive visibility across enterprise', 'Perform behavior-based analytics', 'Enable automated situational awareness']
  },
  ID_AUTOMATION: {
    INITIAL: ['Automate orchestration of non-privileged users', 'Automate self-managed entity provisioning', 'Manual orchestration of privileged identities'],
    ADVANCED: ['Automate orchestration of all identities', 'Integrate across all environments', 'Manual orchestration only for privileged users'],
    OPTIMAL: ['Fully automate all identity orchestration', 'Integrate based on behaviors and deployment needs', 'Enable enrollment-based automation']
  },
  ID_GOVERNANCE: {
    INITIAL: ['Define identity policies for enterprise-wide enforcement', 'Implement with minimal automation', 'Manual policy updates'],
    ADVANCED: ['Implement identity policies with automation', 'Update policies periodically', 'Enforce across enterprise'],
    OPTIMAL: ['Fully automate enterprise-wide identity policies', 'Enable continuous enforcement', 'Enable dynamic policy updates']
  },

  // Devices Pillar Cross-Cutting
  DEV_VISIBILITY: {
    INITIAL: ['Use digital identifiers alongside manual inventory', 'Enable endpoint monitoring where available', 'Begin automated anomaly detection'],
    ADVANCED: ['Automate inventory collection including endpoint monitoring', 'Enable anomaly detection to detect unauthorized devices', 'Monitor all standard user devices'],
    OPTIMAL: ['Automate status collection of all network-connected devices', 'Correlate with identities', 'Track virtual asset provisioning patterns']
  },
  DEV_AUTOMATION: {
    INITIAL: ['Begin using tools and scripts for provisioning', 'Automate configuration and registration', 'Automate deprovisioning'],
    ADVANCED: ['Implement monitoring and enforcement mechanisms', 'Manually disconnect or isolate non-compliant devices', 'Identify vulnerable devices'],
    OPTIMAL: ['Fully automate provisioning and registration', 'Automate monitoring and isolation', 'Automate remediation and deprovisioning']
  },
  DEV_GOVERNANCE: {
    INITIAL: ['Set policies for procurement of new devices', 'Define lifecycle policies for non-traditional devices', 'Conduct regular monitoring and scanning'],
    ADVANCED: ['Set enterprise-wide policies for device lifecycle', 'Include enumeration and accountability', 'Enable some automated enforcement'],
    OPTIMAL: ['Automate policies for lifecycle of all network-connected devices', 'Include virtual assets', 'Enable enterprise-wide automation']
  },

  // Networks Pillar Cross-Cutting
  NET_VISIBILITY: {
    INITIAL: ['Employ network monitoring based on known indicators', 'Develop situational awareness per environment', 'Begin correlating telemetry across traffic types'],
    ADVANCED: ['Deploy anomaly-based network detection', 'Correlate telemetry from multiple sources', 'Incorporate automated threat hunting'],
    OPTIMAL: ['Maintain visibility across all agency networks', 'Enable enterprise-wide situational awareness', 'Automate telemetry correlation across all sources']
  },
  NET_AUTOMATION: {
    INITIAL: ['Begin automated configuration management', 'Define resource lifetime based on policies', 'Automate for some network environments'],
    ADVANCED: ['Use automated change management (CI/CD)', 'Manage configuration lifecycle for all networks', 'Respond to and enforce policies'],
    OPTIMAL: ['Define networks using infrastructure-as-code', 'Automate initiation and expiration', 'Align with changing needs automatically']
  },
  NET_GOVERNANCE: {
    INITIAL: ['Define policies for individual network segments', 'Inherit corporate-wide rules', 'Begin tailored policy implementation'],
    ADVANCED: ['Incorporate automation in implementing policies', 'Facilitate transition from perimeter-focused protections', 'Enable policy automation'],
    OPTIMAL: ['Implement enterprise-wide network policies', 'Enable tailored local controls', 'Enable dynamic updates and secure external connections']
  },

  // Applications Pillar Cross-Cutting
  APP_VISIBILITY: {
    INITIAL: ['Begin automated application profile monitoring', 'Improve log collection and aggregation', 'Enable security monitoring automation'],
    ADVANCED: ['Automate monitoring for most applications', 'Identify application-specific trends', 'Refine processes to address visibility gaps'],
    OPTIMAL: ['Perform continuous dynamic monitoring', 'Maintain enterprise-wide comprehensive visibility', 'Enable real-time application analytics']
  },
  APP_AUTOMATION: {
    INITIAL: ['Periodically modify application configurations', 'Adjust location and access settings', 'Meet security and performance goals'],
    ADVANCED: ['Automate application configurations', 'Respond to operational changes', 'Respond to environmental changes'],
    OPTIMAL: ['Continuously optimize for security and performance', 'Automate all configuration changes', 'Enable self-optimizing applications']
  },
  APP_GOVERNANCE: {
    INITIAL: ['Begin automated policy enforcement for app development', 'Automate software asset management', 'Track software dependencies with SBOM'],
    ADVANCED: ['Implement tiered policies enterprise-wide', 'Cover all app development and deployment aspects', 'Leverage automation for enforcement'],
    OPTIMAL: ['Fully automate policies for app development', 'Incorporate dynamic updates via CI/CD', 'Enable continuous compliance']
  },

  // Data Pillar Cross-Cutting
  DATA_VISIBILITY: {
    INITIAL: ['Obtain visibility based on data inventory', 'Enable automated analysis and correlation', 'Monitor encryption and access attempts'],
    ADVANCED: ['Maintain comprehensive enterprise-wide visibility', 'Employ predictive analytics', 'Enable automated correlation'],
    OPTIMAL: ['Achieve visibility across full data lifecycle', 'Enable robust predictive analytics', 'Support continuous security posture assessment']
  },
  DATA_AUTOMATION: {
    INITIAL: ['Use automated processes for data lifecycle policies', 'Automate security policy implementation', 'Begin automation of data management'],
    ADVANCED: ['Implement policies through automated methods', 'Cover most agency data', 'Enable consistent tiered approach'],
    OPTIMAL: ['Automate data lifecycle to maximum extent', 'Cover all agency data', 'Enable enterprise-wide data automation']
  },
  DATA_GOVERNANCE: {
    INITIAL: ['Define high-level data governance policies', 'Rely on manual segmented implementation', 'Establish governance baseline'],
    ADVANCED: ['Begin integration of data lifecycle enforcement', 'Enable unified definitions', 'Expand governance across enterprise'],
    OPTIMAL: ['Unify data lifecycle policies to maximum extent', 'Enable dynamic enforcement', 'Enable enterprise-wide data governance']
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // ENTERPRISE-LEVEL CROSS-CUTTING (Table 7)
  // ═══════════════════════════════════════════════════════════════════════════

  VISIBILITY: {
    INITIAL: ['Begin automated log collection and analysis', 'Focus on mission critical functions', 'Assess processes for visibility gaps'],
    ADVANCED: ['Expand automated collection enterprise-wide', 'Include virtual environments', 'Enable centralized analysis across sources'],
    OPTIMAL: ['Maintain comprehensive enterprise-wide visibility', 'Enable centralized dynamic monitoring', 'Enable advanced analysis of logs and events']
  },
  AUTOMATION: {
    INITIAL: ['Begin automating orchestration and response', 'Focus on critical mission functions', 'Enable basic automated response'],
    ADVANCED: ['Automate orchestration enterprise-wide', 'Leverage contextual information from multiple sources', 'Inform decisions automatically'],
    OPTIMAL: ['Enable dynamic response to changing requirements', 'Respond to environmental changes', 'Achieve autonomous operations']
  },
  GOVERNANCE: {
    INITIAL: ['Define policies for enterprise-wide enforcement', 'Implement with minimal automation', 'Enable manual updates'],
    ADVANCED: ['Implement tiered tailored policies enterprise-wide', 'Leverage automation for enforcement', 'Incorporate contextual information in decisions'],
    OPTIMAL: ['Fully automate enterprise-wide policies', 'Enable tailored local controls', 'Enable continuous enforcement with dynamic updates']
  }
};

/**
 * Helper: Get configuration steps for advancing to a level.
 * Checks vendor-specific configs first (MATURITY_CONFIGURATIONS), then falls back to simple configs.
 * Returns simple string array for UI consumption.
 */
export function getConfigurationSteps(functionCode, targetLevel) {
  // First check vendor-specific configs (Networks pillar has these)
  const vendorConfigs = MATURITY_CONFIGURATIONS[functionCode]?.[targetLevel];
  if (vendorConfigs && vendorConfigs.length > 0) {
    // Extract the 'steps' field from each vendor config object
    return vendorConfigs.map(cfg => cfg.steps);
  }
  // Fall back to simple configuration steps
  return SIMPLE_CONFIGURATIONS[functionCode]?.[targetLevel] || [];
}
